// Administración de DOCUMENTOS LEGALES versionados (solo administrador del
// sistema). Permite ver todos los documentos con su historial de versiones,
// crear una versión en BORRADOR (opcionalmente clonando el contenido de la
// vigente), editar el borrador, PUBLICARLO (archiva la vigente) y archivar
// versiones. El contenido se edita como secciones (encabezado + párrafo), igual
// que lo sirve el backend.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useApi } from '../api/ApiContext.js';
import { AlertMessage, Badge, Card, Icon, PageHeader, Spinner } from '../ui/components.js';
import { useAsync } from '../ui/useAsync.js';
import type {
  LegalDocumentAdmin,
  LegalKind,
  LegalSection,
  LegalVersion,
} from '../api/endpoints.js';

const STATUS_TONE: Record<LegalVersion['status'], 'success' | 'info' | 'neutral'> = {
  published: 'success',
  draft: 'info',
  archived: 'neutral',
};

/** Etiqueta i18n del tipo de documento legal. */
function kindLabel(t: TFunction, kind: LegalKind): string {
  return t(`legalAdmin.kind.${kind}`, { defaultValue: kind });
}

/** Etiqueta i18n del estado de una versión. */
function statusLabel(t: TFunction, status: LegalVersion['status']): string {
  return t(`legalAdmin.statusLabel.${status}`, { defaultValue: status });
}

/** Estado del editor de una versión (crear o editar borrador). */
interface EditorState {
  mode: 'create' | 'edit';
  kind: LegalKind;
  versionId?: string;
  version: string;
  title: string;
  summary: string;
  sections: LegalSection[];
}

export function LegalAdminView() {
  const { t } = useTranslation();
  const api = useApi();
  const docs = useAsync(() => api.legalAdmin.list(), []);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const items = docs.data?.items ?? [];

  function startCreate(doc: LegalDocumentAdmin) {
    // Clona el contenido de la versión vigente (si la hay) como punto de partida.
    const published = doc.versions.find((v) => v.status === 'published');
    setError(null);
    setNotice(null);
    setEditor({
      mode: 'create',
      kind: doc.kind,
      version: suggestNextVersion(doc.versions),
      title: published?.title ?? doc.title,
      summary: '',
      sections: published ? published.sections.map((s) => ({ ...s })) : [{ h: '', p: '' }],
    });
  }

  function startEdit(doc: LegalDocumentAdmin, v: LegalVersion) {
    setError(null);
    setNotice(null);
    setEditor({
      mode: 'edit',
      kind: doc.kind,
      versionId: v.id,
      version: v.version,
      title: v.title,
      summary: v.summary ?? '',
      sections: v.sections.map((s) => ({ ...s })),
    });
  }

  async function save() {
    if (!editor) return;
    const cleanSections = editor.sections
      .map((s) => ({ h: s.h.trim(), p: s.p.trim() }))
      .filter((s) => s.h !== '' && s.p !== '');
    if (editor.version.trim() === '' || editor.title.trim() === '' || cleanSections.length === 0) {
      setError(t('legalAdmin.errRequired'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (editor.mode === 'create') {
        await api.legalAdmin.createVersion({
          kind: editor.kind,
          version: editor.version.trim(),
          title: editor.title.trim(),
          summary: editor.summary.trim() || null,
          sections: cleanSections,
        });
        setNotice(t('legalAdmin.draftCreated'));
      } else if (editor.versionId) {
        await api.legalAdmin.updateVersion(editor.versionId, {
          version: editor.version.trim(),
          title: editor.title.trim(),
          summary: editor.summary.trim() || null,
          sections: cleanSections,
        });
        setNotice(t('legalAdmin.draftUpdated'));
      }
      setEditor(null);
      docs.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('legalAdmin.errSave'));
    } finally {
      setBusy(false);
    }
  }

  async function publish(v: LegalVersion) {
    if (!window.confirm(t('legalAdmin.publishConfirm', { version: v.version }))) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.legalAdmin.publishVersion(v.id);
      setNotice(t('legalAdmin.published', { version: v.version }));
      docs.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('legalAdmin.errPublish'));
    } finally {
      setBusy(false);
    }
  }

  async function archive(v: LegalVersion) {
    if (!window.confirm(t('legalAdmin.archiveConfirm', { version: v.version }))) return;
    setBusy(true);
    setError(null);
    try {
      await api.legalAdmin.archiveVersion(v.id);
      setNotice(t('legalAdmin.archived', { version: v.version }));
      docs.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('legalAdmin.errArchive'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col">
      <PageHeader
        title={t('legalAdmin.pageTitle')}
        subtitle={t('legalAdmin.pageSubtitle')}
        back
      />
      <div className="flex flex-col px-margin-mobile py-space-md gap-space-md">
        <AlertMessage message={error} />
        {notice && (
          <p className="text-goldmine-mint font-body-sm text-body-sm">✓ {notice}</p>
        )}

        {docs.loading && <Spinner label={t('legalAdmin.loading')} />}
        {docs.error && <p className="text-error font-body-sm text-body-sm">{String(docs.error)}</p>}

        {!docs.loading &&
          items.map((doc) => (
            <Card key={doc.id} title={kindLabel(t, doc.kind)} icon="description">
              <div className="flex flex-col gap-space-sm">
                {doc.versions.length === 0 && (
                  <p className="font-body-sm text-body-sm text-on-surface-variant italic">
                    {t('legalAdmin.noVersions')}
                  </p>
                )}
                {doc.versions.map((v) => (
                  <div
                    key={v.id}
                    className="flex items-center justify-between gap-space-sm p-space-sm rounded-lg bg-surface-deck border border-border-subtle"
                  >
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <div className="flex items-center gap-space-xs">
                        <span className="font-body-md text-body-md text-on-surface font-semibold">
                          v{v.version}
                        </span>
                        <Badge tone={STATUS_TONE[v.status]}>{statusLabel(t, v.status)}</Badge>
                      </div>
                      <span className="font-badge-micro text-badge-micro text-on-surface-variant">
                        {t('legalAdmin.sectionsRef', { count: v.sections.length, hash: v.content_hash.slice(0, 10) })}
                      </span>
                    </div>
                    <div className="flex items-center gap-space-xs shrink-0">
                      {v.status === 'draft' && (
                        <>
                          <button
                            type="button"
                            className="btn-secondary"
                            disabled={busy}
                            onClick={() => startEdit(doc, v)}
                          >
                            <Icon name="edit" className="text-[16px]" /> {t('legalAdmin.edit')}
                          </button>
                          <button
                            type="button"
                            className="btn-primary"
                            disabled={busy}
                            onClick={() => void publish(v)}
                          >
                            <Icon name="publish" className="text-[16px]" /> {t('legalAdmin.publish')}
                          </button>
                        </>
                      )}
                      {v.status !== 'archived' && (
                        <button
                          type="button"
                          className="btn-secondary"
                          disabled={busy}
                          onClick={() => void archive(v)}
                          title={t('legalAdmin.archive')}
                        >
                          <Icon name="archive" className="text-[16px]" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  className="btn-secondary w-full"
                  disabled={busy}
                  onClick={() => startCreate(doc)}
                >
                  <Icon name="add" className="text-[18px]" /> {t('legalAdmin.newVersion')}
                </button>
              </div>
            </Card>
          ))}
      </div>

      {editor && (
        <VersionEditor
          editor={editor}
          busy={busy}
          onChange={setEditor}
          onCancel={() => setEditor(null)}
          onSave={() => void save()}
        />
      )}
    </div>
  );
}

/** Editor modal de una versión (crear/editar borrador). */
function VersionEditor({
  editor,
  busy,
  onChange,
  onCancel,
  onSave,
}: {
  editor: EditorState;
  busy: boolean;
  onChange: (e: EditorState) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  const { t } = useTranslation();
  function setSection(index: number, patch: Partial<LegalSection>) {
    const sections = editor.sections.map((s, i) => (i === index ? { ...s, ...patch } : s));
    onChange({ ...editor, sections });
  }
  function addSection() {
    onChange({ ...editor, sections: [...editor.sections, { h: '', p: '' }] });
  }
  function removeSection(index: number) {
    onChange({ ...editor, sections: editor.sections.filter((_, i) => i !== index) });
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-space-md">
      <div className="w-full max-w-2xl bg-surface-base border border-border-subtle rounded-xl my-space-lg flex flex-col gap-space-md p-space-lg">
        <div className="flex items-center justify-between">
          <h2 className="font-headline-sm text-headline-md text-on-surface font-bold">
            {editor.mode === 'create' ? t('legalAdmin.editorNewTitle') : t('legalAdmin.editorEditTitle')} · {kindLabel(t, editor.kind)}
          </h2>
          <button type="button" onClick={onCancel} className="text-on-surface-variant hover:text-on-surface">
            <Icon name="close" className="text-[22px]" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-space-sm">
          <div className="flex flex-col gap-space-xs">
            <label className="field-label">{t('legalAdmin.version')}</label>
            <input
              className="field-input bg-surface-deck border border-border-subtle rounded-lg px-space-sm h-10"
              value={editor.version}
              placeholder="1.1.0"
              onChange={(e) => onChange({ ...editor, version: e.target.value })}
            />
          </div>
          <div className="flex flex-col gap-space-xs">
            <label className="field-label">{t('legalAdmin.title')}</label>
            <input
              className="field-input bg-surface-deck border border-border-subtle rounded-lg px-space-sm h-10"
              value={editor.title}
              onChange={(e) => onChange({ ...editor, title: e.target.value })}
            />
          </div>
        </div>

        <div className="flex flex-col gap-space-xs">
          <label className="field-label">{t('legalAdmin.summaryLabel')}</label>
          <input
            className="field-input bg-surface-deck border border-border-subtle rounded-lg px-space-sm h-10"
            value={editor.summary}
            onChange={(e) => onChange({ ...editor, summary: e.target.value })}
          />
        </div>

        <div className="flex flex-col gap-space-sm">
          <div className="flex items-center justify-between">
            <span className="section-label text-on-surface-variant">{t('legalAdmin.sections')}</span>
            <button type="button" className="btn-secondary" onClick={addSection}>
              <Icon name="add" className="text-[16px]" /> {t('legalAdmin.addSection')}
            </button>
          </div>
          {editor.sections.map((s, i) => (
            <div key={i} className="flex flex-col gap-space-xs p-space-sm rounded-lg bg-surface-deck border border-border-subtle">
              <div className="flex items-center gap-space-xs">
                <input
                  className="field-input flex-1 bg-surface-rack border border-border-subtle rounded-lg px-space-sm h-9"
                  value={s.h}
                  placeholder={t('legalAdmin.sectionHeadingPlaceholder')}
                  onChange={(e) => setSection(i, { h: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() => removeSection(i)}
                  className="text-on-surface-variant hover:text-priority-urgent"
                  title={t('legalAdmin.removeSection')}
                >
                  <Icon name="delete" className="text-[18px]" />
                </button>
              </div>
              <textarea
                className="field-input bg-surface-rack border border-border-subtle rounded-lg px-space-sm py-space-xs min-h-[80px]"
                value={s.p}
                placeholder={t('legalAdmin.sectionTextPlaceholder')}
                onChange={(e) => setSection(i, { p: e.target.value })}
              />
            </div>
          ))}
        </div>

        <div className="flex items-center gap-space-sm">
          <button type="button" className="btn-cyan flex-1" disabled={busy} onClick={onSave}>
            <Icon name="save" className="text-[18px]" />
            {busy ? t('legalAdmin.saving') : t('legalAdmin.saveDraft')}
          </button>
          <button type="button" className="btn-secondary" disabled={busy} onClick={onCancel}>
            {t('legalAdmin.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Sugiere la siguiente versión incrementando el patch de la mayor existente. */
function suggestNextVersion(versions: LegalVersion[]): string {
  if (versions.length === 0) return '1.0.0';
  const parsed = versions
    .map((v) => v.version.split('.').map((n) => parseInt(n, 10)))
    .filter((parts) => parts.length === 3 && parts.every((n) => Number.isFinite(n)))
    .sort((a, b) => a[0]! - b[0]! || a[1]! - b[1]! || a[2]! - b[2]!);
  const latest = parsed[parsed.length - 1];
  if (!latest) return '1.1.0';
  return `${latest[0]}.${latest[1]! + 1}.0`;
}
